import { create } from "zustand";
import { Backend } from "../url";

interface TemplateData {
  head: Record<
    string,
    {
      smiles: string;
      iupac: string;
      parent: string;
      image: string;
      molecularWeight: number;
      casNumber: string;
    }
  >;
  linker: Record<
    string,
    {
      smiles: string;
      iupac: string;
      parent: string;
      image: string;
      molecularWeight: number;
      casNumber: string;
    }
  >;
  tail1: Record<
    string,
    {
      smiles: string;
      iupac: string;
      parent: string;
      image: string;
      molecularWeight: number;
      casNumber: string;
    }
  >;
  tail2: Record<
    string,
    {
      smiles: string;
      iupac: string;
      parent: string;
      image: string;
      molecularWeight: number;
      casNumber: string;
    }
  >;
}

interface TemplateState {
  TemplateData: TemplateData | null;
  lastUpdated: number | null;
  isLoading: boolean;
  error: string | null;
  fetchTemplateData: () => Promise<void>;
  resetTemplateData: () => void;
  needsRefresh: () => boolean;
}

const REFRESH_INTERVAL = 1 * 24 * 60 * 60 * 1000;

const useTemplateStore = create<TemplateState>()((set, get) => ({
  TemplateData: null,
  lastUpdated: null,
  isLoading: false,
  error: null,

  needsRefresh: () => {
    const { lastUpdated } = get();
    if (!lastUpdated) return true;
    return Date.now() - lastUpdated > REFRESH_INTERVAL;
  },

  fetchTemplateData: async () => {
    if (get().TemplateData && !get().needsRefresh() && !get().isLoading) {
      return;
    }

    if (get().isLoading) return;

    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${Backend}/search/templates/`);
      if (!response.ok) {
        throw new Error(`API Request Fail: ${response.status}`);
      }

      const data = await response.json();
      set({
        TemplateData: data,
        lastUpdated: Date.now(),
        isLoading: false,
      });
    } catch (error) {
      console.error("Data load error:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Unknown error occurred while fetching template data.",
        isLoading: false,
      });
    }
  },

  resetTemplateData: () => {
    set({
      TemplateData: null,
      lastUpdated: null,
      error: null,
      isLoading: false,
    });
  },
}));

export default useTemplateStore;
